import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogContent,
  useTheme,
  InputBase,
} from "@mui/material";

// ── Icons ────────────────────────────────────────────────────────────────────
const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M3 3l10 10M13 3L3 13" stroke="#888" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const UserIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.7" />
    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);

const CardIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
    <rect x="2" y="5" width="20" height="14" rx="3" stroke="currentColor" strokeWidth="1.7" />
    <path d="M2 10h20" stroke="currentColor" strokeWidth="1.7" />
  </svg>
);

const LockIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
    <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.7" />
    <path d="M8 11V7a4 4 0 018 0v4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);

const PinIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" stroke="currentColor" strokeWidth="1.7" />
    <circle cx="12" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.7" />
  </svg>
);

// ── Styled input ─────────────────────────────────────────────────────────────
const FormField = ({ label, icon, placeholder, value, onChange, maxLength, type = "text", half = false }) => {
  const [focused, setFocused] = useState(false);
  const theme = useTheme();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 600,
          color: theme.palette.text.disabled,
          textTransform: "uppercase",
          letterSpacing: "0.07em",
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 1.25,
          border: `1.5px solid ${focused ? "#F5C518" : theme.palette.divider}`,
          borderRadius: "10px",
          backgroundColor: "#fff",
          boxShadow: focused ? "0 0 0 3px rgba(245,197,24,0.12)" : "none",
          transition: "border-color .15s, box-shadow .15s",
        }}
      >
        {icon && (
          <Box sx={{ color: focused ? "#F5C518" : theme.palette.text.disabled, flexShrink: 0, transition: "color .15s" }}>
            {icon}
          </Box>
        )}
        <InputBase
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          inputProps={{ maxLength }}
          sx={{
            flex: 1,
            fontSize: 13.5,
            color: theme.palette.text.primary,
            "& input::placeholder": { color: theme.palette.text.disabled, opacity: 1 },
            "& input": { p: 0, fontFamily: "inherit" },
          }}
        />
      </Box>
    </Box>
  );
};

// ── Live card preview ─────────────────────────────────────────────────────────
const CardPreview = ({ name, number, expiry }) => {
  const displayNumber =
    number.replace(/\s/g, "").padEnd(16, "·").match(/.{1,4}/g).join(" ");

  return (
    <Box
      sx={{
        mx: 3,
        mt: 2.5,
        height: 105,
        borderRadius: "14px",
        background: "linear-gradient(135deg, #1a1a2e 0%, #2d2d4e 100%)",
        px: 2.75,
        py: 2.25,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Decorative circles */}
      <Box sx={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, borderRadius: "50%", backgroundColor: "rgba(255,255,255,0.04)" }} />
      <Box sx={{ position: "absolute", bottom: -40, right: 30, width: 100, height: 100, borderRadius: "50%", backgroundColor: "rgba(255,255,255,0.05)" }} />

      {/* Chip */}
      <Box
        sx={{
          position: "absolute",
          top: 20,
          right: 24,
          width: 28,
          height: 20,
          borderRadius: "4px",
          background: "linear-gradient(135deg, #F5C518 0%, #d4a800 100%)",
        }}
      />

      {/* Card number */}
      <Typography
        sx={{
          fontSize: 15,
          fontWeight: 600,
          letterSpacing: "0.18em",
          color: "rgba(255,255,255,0.9)",
          mb: 1.75,
          fontVariantNumeric: "tabular-nums",
          fontFamily: "monospace",
        }}
      >
        {displayNumber}
      </Typography>

      {/* Bottom row */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
        <Box>
          <Typography sx={{ fontSize: 9, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.25 }}>
            Cardholder
          </Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.85)", letterSpacing: "0.04em" }}>
            {name.toUpperCase() || "FULL NAME"}
          </Typography>
        </Box>
        <Box sx={{ textAlign: "right" }}>
          <Typography sx={{ fontSize: 9, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.25 }}>
            Expires
          </Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.85)" }}>
            {expiry || "MM/YY"}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// ── Toggle ────────────────────────────────────────────────────────────────────
const Toggle = ({ value, onChange }) => {
  const yellow = "#F5C518";
  return (
    <Box
      onClick={() => onChange(!value)}
      sx={{
        width: 40,
        height: 22,
        borderRadius: "99px",
        backgroundColor: value ? yellow : "#e5e7eb",
        position: "relative",
        cursor: "pointer",
        transition: "background .2s",
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 3,
          left: value ? "calc(100% - 19px)" : 3,
          width: 16,
          height: 16,
          borderRadius: "50%",
          backgroundColor: "#fff",
          boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
          transition: "left .2s cubic-bezier(.4,0,.2,1)",
        }}
      />
    </Box>
  );
};

// ── Main modal ────────────────────────────────────────────────────────────────
const AddPaymentMethodModal = ({ open, onClose, onSave }) => {
  const theme = useTheme();

  const yellow = theme.palette.scraptradeYellow?.main || "#F5C518";
  const yellowDark = theme.palette.scraptradeYellow?.dark || "#d4a800";
  const dark = theme.palette.scraptradeDark?.main || "#111111";

  const [form, setForm] = useState({
    name: "",
    number: "",
    expiry: "",
    cvv: "",
    address: "",
    saveAsDefault: true,
  });

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  // Card number formatter — groups of 4
  const handleCardNumber = (e) => {
    const raw = e.target.value.replace(/\D/g, "").substring(0, 16);
    const formatted = raw.match(/.{1,4}/g)?.join(" ") || raw;
    setForm((f) => ({ ...f, number: formatted }));
  };

  // Expiry formatter — MM / YY
  const handleExpiry = (e) => {
    const raw = e.target.value.replace(/\D/g, "").substring(0, 4);
    const formatted = raw.length >= 3 ? raw.substring(0, 2) + " / " + raw.substring(2) : raw;
    setForm((f) => ({ ...f, expiry: formatted }));
  };

  const handleSave = () => {
    onSave?.(form);
    handleClose();
  };

  const handleClose = () => {
    setForm({ name: "", number: "", expiry: "", cvv: "", address: "", saveAsDefault: true });
    onClose();
  };

  const isValid =
    form.name.trim().length > 1 &&
    form.number.replace(/\s/g, "").length === 16 &&
    form.expiry.length >= 4 &&
    form.cvv.length >= 3 &&
    form.address.trim().length > 3;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "22px",
          boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
          overflow: "hidden",
        },
      }}
      BackdropProps={{
        sx: { backdropFilter: "blur(3px)", backgroundColor: "rgba(0,0,0,0.45)" },
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 3,
          pt: 2.75,
          pb: 2.25,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: theme.palette.text.primary }}>
            Add Payment Method
          </Typography>
          <Typography sx={{ fontSize: 12, color: theme.palette.text.disabled, mt: 0.25 }}>
            Your card details are encrypted and secure.
          </Typography>
        </Box>
        <Box
          onClick={handleClose}
          sx={{
            width: 32,
            height: 32,
            borderRadius: "8px",
            border: `1px solid ${theme.palette.divider}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            flexShrink: 0,
            transition: "all .12s",
            "&:hover": { backgroundColor: theme.palette.grey[50], borderColor: "#ccc" },
          }}
        >
          <CloseIcon />
        </Box>
      </Box>

      <DialogContent sx={{ p: 0 }}>

        {/* ── Live card preview ── */}
        <CardPreview
          name={form.name}
          number={form.number}
          expiry={form.expiry}
        />

        {/* ── Form ── */}
        <Box sx={{ px: 3, pt: 2.5, display: "flex", flexDirection: "column", gap: 1.75 }}>

          {/* Cardholder name */}
          <FormField
            label="Cardholder Name"
            icon={<UserIcon />}
            placeholder="e.g. John Smith"
            value={form.name}
            onChange={set("name")}
          />

          {/* Card number */}
          <FormField
            label="Card Number"
            icon={<CardIcon />}
            placeholder="1234 5678 9012 3456"
            value={form.number}
            onChange={handleCardNumber}
            maxLength={19}
            type="tel"
          />

          {/* Expiry + CVV row */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
            <FormField
              label="Expiry Date"
              placeholder="MM / YY"
              value={form.expiry}
              onChange={handleExpiry}
              maxLength={7}
              type="tel"
            />
            <FormField
              label="CVV"
              icon={<LockIcon />}
              placeholder="•••"
              value={form.cvv}
              onChange={set("cvv")}
              maxLength={4}
              type="tel"
            />
          </Box>

          {/* Billing address */}
          <FormField
            label="Billing Address"
            icon={<PinIcon />}
            placeholder="123 Main Street, City, Postcode"
            value={form.address}
            onChange={set("address")}
          />

          {/* Save as default toggle */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              pt: 1.75,
              pb: 0.5,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: theme.palette.text.primary }}>
                Save as default
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: theme.palette.text.disabled, mt: 0.25 }}>
                Use this card for all future payments
              </Typography>
            </Box>
            <Toggle
              value={form.saveAsDefault}
              onChange={(v) => setForm((f) => ({ ...f, saveAsDefault: v }))}
            />
          </Box>
        </Box>

        {/* ── Footer ── */}
        <Box sx={{ display: "flex", gap: 1.25, px: 3, pt: 2, pb: 3 }}>
          <Button
            onClick={handleClose}
            sx={{
              flex: 1,
              py: 1.25,
              border: `1.5px solid ${theme.palette.divider}`,
              borderRadius: "10px",
              color: theme.palette.text.secondary,
              fontSize: 13.5,
              fontWeight: 600,
              textTransform: "none",
              "&:hover": { borderColor: "#ccc", backgroundColor: theme.palette.grey[50] },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!isValid}
            sx={{
              flex: 2,
              py: 1.25,
              backgroundColor: yellow,
              color: dark,
              fontSize: 13.5,
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "10px",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: yellowDark,
                boxShadow: `0 4px 14px ${yellow}4d`,
              },
              "&.Mui-disabled": {
                backgroundColor: theme.palette.grey[100],
                color: theme.palette.text.disabled,
              },
            }}
          >
            Save Card
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AddPaymentMethodModal;